import { useState } from "react";
import { RichTextContentRenderer } from "../app/RichTextContentRenderer";
import { knowledgeArticleViewCopy } from "../app/i18n";
import { knowledgeArticleStatusBadgeClass, knowledgeArticleStatusLabel } from "../app/articleStatus";
import { getIntlLocale, type AppLanguage } from "../app/language";
import { UserAvatarBadge } from "../app/ui";
import type { KnowledgeArticle, KnowledgeCollection, WorkspaceMember } from "../app/types";
import { getInitials, resolveTimeZone, resolveWorkspaceMemberByEmail } from "../app/utils";

type KnowledgeArticleViewProps = {
  article: KnowledgeArticle;
  collection: KnowledgeCollection | null;
  teamMembers: WorkspaceMember[];
  currentUserEmail: string | null;
  language: AppLanguage;
  timezone: string;
  canManageArticle?: boolean;
  onBack: () => void;
  onEdit: () => void;
  onDelete: () => Promise<void>;
  onToggleFeatured: () => Promise<void>;
};

export function KnowledgeArticleView({ article, collection, teamMembers, currentUserEmail, language, timezone, canManageArticle = true, onBack, onEdit, onDelete, onToggleFeatured }: KnowledgeArticleViewProps) {
  const copy = knowledgeArticleViewCopy[language];
  const hasContent = Boolean(article.content?.html?.trim());
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingFeatured, setIsUpdatingFeatured] = useState(false);
  const memberNameByEmail = new Map(teamMembers.map(member => [member.email.trim().toLowerCase(), member.name]));
  const normalizedCurrentUserEmail = currentUserEmail?.toLowerCase() ?? null;
  const articleVisibility = article.visibility ?? "workspace";
  const articleOwnerUserEmail = article.ownerUserEmail ?? null;
  const articleSharedWithUserEmails = Array.isArray(article.sharedWithUserEmails) ? article.sharedWithUserEmails : [];
  const isPrivateArticle = articleVisibility === "private";
  const isOwner = Boolean(normalizedCurrentUserEmail) && articleOwnerUserEmail === normalizedCurrentUserEmail;
  const sharedWithLabels = articleSharedWithUserEmails.map(email => memberNameByEmail.get(email) ?? email);
  const authorMember = resolveWorkspaceMemberByEmail(teamMembers, articleOwnerUserEmail);
  const formatMetaDate = (value: string) => {
    const resolvedTimeZone = resolveTimeZone(timezone);

    return new Intl.DateTimeFormat(getIntlLocale(language), {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      ...(resolvedTimeZone ? { timeZone: resolvedTimeZone, timeZoneName: "short" } : {}),
    }).format(new Date(value));
  };

  const handleDelete = async () => {
    if (typeof window !== "undefined") {
      const confirmed = window.confirm(copy.page.confirmDelete(article.title));

      if (!confirmed) {
        return;
      }
    }

    setIsDeleting(true);

    try {
      await onDelete();
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleFeatured = async () => {
    setIsUpdatingFeatured(true);

    try {
      await onToggleFeatured();
    } finally {
      setIsUpdatingFeatured(false);
    }
  };

  return (
    <section className="space-y-8 p-8">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={onBack}
            className="rounded-full bg-white px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.05)] transition-colors hover:bg-[#f9fafb]"
          >
            ← {copy.page.back}
          </button>
          <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.32em] text-[#98a2b3]">{isPrivateArticle ? copy.page.privateEyebrow : copy.page.sharedEyebrow}</p>
          <div className="mt-3 flex items-end gap-2">
            <h1 className="max-w-4xl font-headline text-5xl font-extrabold tracking-[-0.06em] text-[#101828]">{article.title}</h1>
            {canManageArticle && !isPrivateArticle ? (
              <button
                type="button"
                onClick={handleToggleFeatured}
                disabled={isUpdatingFeatured}
                aria-label={article.isFeatured ? copy.pin.unpin : copy.pin.pin}
                title={article.isFeatured ? copy.pin.unpin : copy.pin.pin}
                className={`mb-1 inline-flex h-5.5 w-5.5 shrink-0 items-center justify-center bg-transparent transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${article.isFeatured ? "text-[#006949]" : "text-[#667085]"}`}
              >
                <span className="material-symbols-outlined text-[11px] leading-none">{article.isFeatured ? "keep" : "keep_off"}</span>
              </button>
            ) : null}
          </div>
          <p className="mt-3 max-w-3xl text-sm text-[#667085]">{article.summary || copy.page.noSummary}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {isPrivateArticle ? <span className="rounded-full bg-[#eef4ff] px-3 py-1 text-[11px] font-semibold text-[#175cd3]">{copy.page.private}</span> : null}
            {isPrivateArticle ? <span className="rounded-full bg-[#f2f4f7] px-3 py-1 text-[11px] font-semibold text-[#475467]">{isOwner ? copy.page.ownArticle : copy.page.sharedWithYou}</span> : null}
          </div>
        </div>

        {canManageArticle ? (
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleDelete}
              disabled={isDeleting}
              className="rounded-xl border border-[#fda29b] bg-white px-5 py-3 text-sm font-bold text-[#b42318] transition-colors hover:bg-[#fef3f2] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isDeleting ? copy.page.deleting : copy.page.deleteArticle}
            </button>
            <button type="button" onClick={onEdit} className="brand-primary-button rounded-xl px-5 py-3 text-sm font-bold">
              {copy.page.editArticle}
            </button>
          </div>
        ) : null}
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.25fr)_340px]">
        <article className="rounded-[32px] border border-white/60 bg-white p-8 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
          <div className="space-y-6">
            {hasContent ? <RichTextContentRenderer content={article.content} /> : <p className="text-sm text-[#667085]">{copy.page.noContent}</p>}
          </div>
        </article>

        <aside className="space-y-6">
          <section className="rounded-[32px] border border-white/60 bg-white p-6 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.meta.eyebrow}</p>
            <div className="mt-5 space-y-3">
              <div className="rounded-2xl bg-[#f9fafb] px-4 py-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.meta.status}</p>
                <span className={`mt-2 inline-flex rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${knowledgeArticleStatusBadgeClass(article.status)}`}>{knowledgeArticleStatusLabel(article.status, language)}</span>
              </div>
              {[
                [copy.meta.collection, collection?.name ?? copy.meta.unassigned],
                [copy.meta.featured, isPrivateArticle ? copy.meta.hiddenForPrivate : article.isFeatured ? copy.meta.pinned : copy.meta.notPinned],
                [copy.meta.category, article.audience],
                [copy.meta.readTime, article.estimatedReadTime],
                [copy.meta.created, formatMetaDate(article.createdAt)],
                [copy.meta.updated, formatMetaDate(article.updatedAt)],
              ].map(([label, value]) => (
                <div key={label} className="rounded-2xl bg-[#f9fafb] px-4 py-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#98a2b3]">{label}</p>
                  <p className="mt-2 text-sm font-semibold text-[#101828]">{value}</p>
                </div>
              ))}
              <div className="rounded-2xl bg-[#f9fafb] px-4 py-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.meta.author}</p>
                <div className="mt-3 flex items-center gap-3">
                  <UserAvatarBadge
                    avatarUrl={authorMember?.avatarUrl ?? null}
                    initials={authorMember?.initials ?? getInitials(memberNameByEmail.get(articleOwnerUserEmail ?? "") ?? articleOwnerUserEmail ?? copy.meta.unknown)}
                    className="brand-primary-avatar flex h-10 w-10 items-center justify-center rounded-full text-xs font-bold"
                  />
                  <div>
                    <p className="text-sm font-semibold text-[#101828]">{memberNameByEmail.get(articleOwnerUserEmail ?? "") ?? articleOwnerUserEmail ?? copy.meta.unknown}</p>
                    {articleOwnerUserEmail ? <p className="mt-1 text-xs text-[#667085]">{articleOwnerUserEmail}</p> : null}
                  </div>
                </div>
              </div>
              {isPrivateArticle ? (
                <div className="rounded-2xl bg-[#f9fafb] px-4 py-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.meta.owner}</p>
                  <p className="mt-2 text-sm font-semibold text-[#101828]">{memberNameByEmail.get(articleOwnerUserEmail ?? "") ?? articleOwnerUserEmail ?? copy.meta.unknown}</p>
                </div>
              ) : null}
              {isPrivateArticle ? (
                <div className="rounded-2xl bg-[#f9fafb] px-4 py-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.meta.sharedWith}</p>
                  {sharedWithLabels.length > 0 ? (
                    <div className="mt-3 space-y-3">
                      {articleSharedWithUserEmails.map(email => {
                        const member = resolveWorkspaceMemberByEmail(teamMembers, email);

                        return (
                          <div key={email} className="flex items-center gap-3">
                            <UserAvatarBadge
                              avatarUrl={member?.avatarUrl ?? null}
                              initials={member?.initials ?? getInitials(memberNameByEmail.get(email) ?? email)}
                              className="brand-primary-avatar flex h-9 w-9 items-center justify-center rounded-full text-[11px] font-bold"
                            />
                            <div>
                              <p className="text-sm font-semibold text-[#101828]">{memberNameByEmail.get(email) ?? email}</p>
                              <p className="text-xs text-[#667085]">{email}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="mt-2 text-sm font-semibold text-[#101828]">{copy.meta.ownerOnly}</p>
                  )}
                </div>
              ) : null}
            </div>
          </section>

          <section className="rounded-[32px] border border-white/60 bg-white p-6 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.keywords.eyebrow}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {article.keywords.length > 0 ? article.keywords.map(keyword => <span key={keyword} className="rounded-full bg-[#f2f4f7] px-3 py-1 text-[11px] font-semibold text-[#475467]">{keyword}</span>) : <p className="text-sm text-[#667085]">{copy.keywords.empty}</p>}
            </div>
          </section>
        </aside>
      </div>
    </section>
  );
}
